import { AccountsService, PlanName } from 'base/accounts_service';
import { AccountStore } from 'base/account_store';
import { AnalyticsService } from 'base/analytics';
import { Asset } from 'base/asset';
import { getProjectUrl } from 'base/blocks_routes';
import { relativeTime } from 'base/helpers';
import { Avatar } from 'base/ui/avatar';
import { Banner } from 'base/ui/banner';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import {
  GenericErrorDialog,
  getLimitErrorDialog,
  LoadingDialog,
} from 'base/ui/error_dialogs';
import { Icon } from 'base/ui/icon';
import {
  IconLock,
  IconOption,
  IconTop,
  IconUpgrade,
  IconVideo,
} from 'base/ui/icons';
import { InfiniteScroll } from 'base/ui/infinite_scroll';
import { FormInput } from 'base/ui/input';
import { Inline, Rows } from 'base/ui/layout';
import { LoadingDots } from 'base/ui/loading_dots';
import { MenuButton, useRightClickMenu } from 'base/ui/menu';
import { menuDivider, menuItem } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import {
  TextRegular,
  TextRegularLight,
  TextSmallLight,
} from 'base/ui/typography';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { ProFeatureGateDialog } from 'blocks/billing/feature_gate_dialog';
import {
  BlocksService,
  OwnershipType,
  ProjectInfo,
} from 'blocks/blocks_service';
import { showPaymentProblemToast } from 'blocks/editor/session/payment_problem_toast';
import { PresenceAvatars } from 'blocks/editor/session/presence';
import { loadProject } from 'blocks/sync/sync';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { ProjectsDataStore } from '../project_data/projects_data_store';
import { invalidateProjectsStore } from '../project_data/projects_data_store_manager';
import { ConfirmDeleteDialog } from './confirm_delete';
import styles from './projects_list.css';
import {
  ErrorState,
  getTeamProjectsLabel,
  TeamEmptyState,
  TeamUpsellCard,
} from './states';
/** Assumes the user is logged in */
export const ProjectsList = observer(function _ProjectsList({
  accountStore,
  filterOwnership,
  blocksService,
  accountsService,
  toastQueue,
  projectsListStore,
  analytics,
}: {
  accountStore: AccountStore;
  filterOwnership: OwnershipType | undefined;
  blocksService: BlocksService;
  accountsService: AccountsService;
  toastQueue: ToastQueue;
  projectsListStore: ProjectsDataStore;
  analytics: AnalyticsService;
}) {
  const { state } = projectsListStore;
  const numPlaceholders = projectsListStore.projectCount || 8;

  const hasPaymentProblem =
    state.type === 'loaded' && state.res.flags.hasPaymentProblem;
  React.useEffect(() => {
    if (!hasPaymentProblem) return;
    return showPaymentProblemToast(accountsService, toastQueue);
  }, [hasPaymentProblem, accountsService, toastQueue]);

  const callBackLoader = useCallback(
    async (continuation?: string) => {
      if (!continuation) {
        // Initial load
        if (
          projectsListStore.initialLoading &&
          projectsListStore.projects.length === 0
        ) {
          await projectsListStore.loadProjects();
        }
        return {
          continuation: projectsListStore.nextPageUrl || undefined,
          items: [...projectsListStore.projects],
        };
      } else {
        // Load next page
        return projectsListStore.loadMore(continuation);
      }
    },
    [projectsListStore],
  );

  const teamProjectsLabel = getTeamProjectsLabel(
    state.type === 'loaded' ? state.res : undefined,
  );

  if (state.type == 'error') {
    return <ErrorState error={state.error} />;
  }
  if (state.type == 'loading') {
    return (
      <div className={styles.list}>
        {Array.from({
          // +1 for the demo video
          length: numPlaceholders + 1,
        }).map((_, i) => (
          <div
            className={styles.placeholder}
            key={i}
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
    );
  }
  const projects = state.res.projects;

  if (filterOwnership === 'team') {
    if (state.res?.flags.teamFeaturesRequireUpgrade) {
      return <TeamUpsellCard />;
    }
    if (projects.length === 0) {
      // Don't show the demo video for team projects - show the team-specific instructions
      return <TeamEmptyState teamProjectsLabel={teamProjectsLabel} />;
    }
  }

  function render() {
    return (
      <div className={styles.list}>
        {projects.map((project) => (
          <ProjectTile
            key={project.token}
            project={project}
            teamProjectsLabel={teamProjectsLabel}
            reloadList={() => projectsListStore.loadProjects()}
            blocksService={blocksService}
            accountStore={accountStore}
            projectsListStore={projectsListStore}
            analytics={analytics}
          />
        ))}
        {filterOwnership !== 'team' && <DemoVideoTile />}
      </div>
    );
  }

  return (
    <Rows spacing={32}>
      <InfiniteScroll
        loader={callBackLoader}
        render={render}
        LoadingState={LoadingDots}
        ErrorState={ErrorState}
      />
    </Rows>
  );
});

function QuotaInfo({ limit, used }: { limit?: number; used: number }) {
  // Only show when we're at the limit
  const show = limit != null && used >= limit;
  if (!show) return null;

  const upgradeHref = authRouteToUrl({
    path: 'upgrade',
    context: { redirect: getCurrentLocationForRedirect() },
  });

  return (
    <Banner
      variant="primary"
      icon={IconUpgrade}
      actions={<Button label="Upgrade" href={upgradeHref} />}
    >
      <Rows spacing={4}>
        <TextRegular>You've reached your project limit.</TextRegular>
        <TextSmallLight>
          Upgrade your plan to create unlimited projects.
        </TextSmallLight>
      </Rows>
    </Banner>
  );
}

const PROJECT_TOKEN_REGEX = /^P([0-9]+)_([^/]+)$/;

export function ProjectTile({
  project,
  teamProjectsLabel,
  reloadList,
  blocksService,
  accountStore,
  projectsListStore,
  analytics,
}: {
  project: ProjectInfo;
  teamProjectsLabel: string;
  reloadList: () => Promise<void>;
  blocksService: BlocksService;
  accountStore: AccountStore;
  projectsListStore: ProjectsDataStore;
  analytics: AnalyticsService;
}) {
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [confirmingMoveToUser, setConfirmingMoveToUser] = React.useState(false);
  const [renamingProject, setRenamingProject] = React.useState(false);
  const [statusDialog, setStatusDialog] = React.useState<
    React.ReactNode | undefined
  >();

  const href = getProjectUrl(project.token);
  const name = project.name || 'Untitled Project';

  const planName = accountStore.member?.planName ?? PlanName.FREE;

  const proFeatureGateDialog = (
    <ProFeatureGateDialog
      currentPlan={planName}
      initialFeatureKey="projects"
      onClose={() => setStatusDialog(undefined)}
      analytics={analytics}
    />
  );

  const makeErrorHandler = (title: string) => (e: unknown) => {
    const LimitErrorDialog = getLimitErrorDialog(e);
    setStatusDialog(
      LimitErrorDialog ? (
        proFeatureGateDialog
      ) : (
        <GenericErrorDialog
          title={title}
          error={e}
          dismiss={() => setStatusDialog(undefined)}
        />
      ),
    );
  };
  const doDelete = async () => {
    await blocksService
      .projectDelete({ projectToken: project.token })
      .then(() => {
        // Remove from current store
        projectsListStore.removeProject(project.token);

        // Invalidate other stores that might have this project
        if (project.ownershipType === 'team') {
          invalidateProjectsStore({ ownershipType: 'team' });
        } else {
          invalidateProjectsStore({ ownershipType: 'user' });
        }
        const [projectId] = project.token.match(PROJECT_TOKEN_REGEX) ?? [];
        analytics.track({
          event: 'dashboard:project_delete',
          project_id: projectId,
        });
        return reloadList();
      })
      .catch(makeErrorHandler('Error deleting project'))
      .finally(() => setConfirmingDelete(false));
  };
  const doDuplicate = () => {
    if (statusDialog) return;

    if (projectsListStore.atProjectLimit) {
      return setStatusDialog(proFeatureGateDialog);
    }

    setStatusDialog(<LoadingDialog title={`Duplicating ${name}...`} />);
    blocksService
      .projectDuplicate({ projectToken: project.token })
      .then(reloadList)
      .then(() => {
        const [projectId] = project.token.match(PROJECT_TOKEN_REGEX) ?? [];
        analytics.track({
          event: 'dashboard:project_duplicate',
          project_id: projectId,
        });
        setStatusDialog(undefined);
      })
      .catch(makeErrorHandler('Error duplicating project'));
  };
  const doMoveToUser = async () => {
    await blocksService
      .projectUpdateSharingSettings({
        projectToken: project.token,
        ownershipType: 'user',
      })
      .then(() => {
        // Invalidate both stores since the project moved between them
        invalidateProjectsStore({ ownershipType: 'team' });
        invalidateProjectsStore({ ownershipType: 'user' });
        return reloadList();
      })
      .catch(makeErrorHandler('Error moving project'))
      .finally(() => setConfirmingMoveToUser(false));
    analytics.track({
      event: 'project:move_button_click',
      button_location: 'dashboard',
      move_to: 'user',
    });
  };
  const doMoveToTeam = () => {
    if (statusDialog) return;
    setStatusDialog(<LoadingDialog title={`Moving ${name}...`} />);
    blocksService
      .projectUpdateSharingSettings({
        projectToken: project.token,
        ownershipType: 'team',
      })
      .then(() => {
        // Invalidate both stores since the project moved between them
        invalidateProjectsStore({ ownershipType: 'team' });
        invalidateProjectsStore({ ownershipType: 'user' });
        return reloadList();
      })
      .then(() => setStatusDialog(undefined))
      .catch(makeErrorHandler('Error moving project'));
    analytics.track({
      event: 'project:move_button_click',
      button_location: 'dashboard',
      move_to: 'team',
    });
  };
  const onChangeName = async (name: string) => {
    try {
      await blocksService.projectRename({
        projectToken: project.token,
        name,
      });
      // We have to load the project to update the name field inside the project blob.
      const loaded = await loadProject({
        blocksService,
        projectToken: project.token,
        hideFromPresence: true,
      });
      loaded.project.name = name;
      await loaded.sync.syncNow();
      loaded.sync.disconnect();
      reloadList();
    } catch (error) {
      makeErrorHandler('Error renaming project')(error);
    }
    setRenamingProject(false);
  };

  const moveMenuItem =
    project.ownershipType === 'team'
      ? menuItem({
          label: 'Move to personal workspace',
          onClick: () => setConfirmingMoveToUser(true),
        })
      : project.ownershipType === 'user' &&
        accountStore.member?.productRestrictions.teamEnabled
      ? menuItem({
          label: `Move to ${teamProjectsLabel}`,
          onClick: doMoveToTeam,
        })
      : null;
  const menu = [
    menuItem({ label: 'Open in new tab', href, target: '_blank' }),
    menuDivider(),
    menuItem({ label: 'Duplicate', onClick: doDuplicate }),
    menuItem({ label: 'Delete', onClick: () => setConfirmingDelete(true) }),
    menuItem({ label: 'Rename', onClick: () => setRenamingProject(true) }),
  ];
  if (moveMenuItem) menu.push(menuDivider(), moveMenuItem);

  const [onContextMenu, rightClickMenu] = useRightClickMenu(menu);

  return (
    <div className={styles.project} onContextMenu={onContextMenu}>
      <a
        href={href}
        className={classNames(
          styles.projectTile,
          project.locked && styles.locked,
        )}
        draggable={false}
      >
        <div className={styles.projectTileText}>
          <TextRegular lineClamp={1}>{name}</TextRegular>
          <Inline spacing={8} yalign="center">
            {project.locked && (
              <Icon icon={IconLock} size={14} color="colorGrey" />
            )}
            {project.ownershipType === 'team' && project.team && (
              <TeamAvatar team={project.team} size="tiny" />
            )}
            <TextSmallLight lineClamp={1} color="grey">
              {project.locked ? 'View only · ' : ''}
              {project.ownershipType === 'team' ? 'Team · ' : ''}
              Edited {relativeTime(project.updatedAt).toLowerCase()}
            </TextSmallLight>
          </Inline>
        </div>
        <PresenceAvatars
          presence={{
            sessions: project.sessions,
            mySessionId: undefined,
          }}
          maxAvatars={2}
          background={project.locked ? 'grey' : 'white'}
          overlapAvatars={true}
          bordered={false}
        />
      </a>
      <div className={styles.projectMenu}>
        <MenuButton
          icon={IconOption}
          menu={menu}
          search="hidden"
          variant="tertiary"
          onContextMenu={stopPropagation}
        />
      </div>
      {rightClickMenu}
      {confirmingDelete && (
        <ConfirmDeleteDialog
          name={name}
          doDelete={doDelete}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
      {confirmingMoveToUser && (
        <ConfirmMoveToUserDialog
          name={name}
          doMove={doMoveToUser}
          onCancel={() => setConfirmingMoveToUser(false)}
        />
      )}
      {renamingProject && (
        <RenameDialog
          name={name}
          onCancel={() => setRenamingProject(false)}
          onConfirm={onChangeName}
        />
      )}
      {statusDialog}
    </div>
  );
}

export function TeamAvatar({
  team,
  size,
}: {
  team: { name: string; color: string; avatar?: Asset } | undefined;
  size: 'tiny' | 'small';
}) {
  return (
    <Avatar
      shape="square"
      size={size}
      avatar={team?.avatar}
      color={team?.color}
      firstName={team?.name}
    />
  );
}

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation();
}

export function ConfirmMoveToUserDialog({
  name,
  doMove,
  onCancel,
}: {
  name: string;
  doMove: () => Promise<void>;
  onCancel: () => void;
}) {
  const [isLoading, setIsLoading] = React.useState(false);
  const onConfirm = () => {
    if (isLoading) return;
    setIsLoading(true);
    doMove().finally(() => setIsLoading(false));
  };

  return (
    <Dialog
      title="Are you sure?"
      onClose={onCancel}
      buttons={
        <>
          <Button label="Cancel" onClick={onCancel} size="large" />
          <Button
            variant="primary"
            label="Move"
            onClick={onConfirm}
            size="large"
            loading={isLoading}
          />
        </>
      }
    >
      <TextRegularLight>
        If you move <TextRegular tag="span">{name}</TextRegular> other members
        of your team will lose access to it.
      </TextRegularLight>
    </Dialog>
  );
}

function RenameDialog({
  name,
  onCancel,
  onConfirm,
}: {
  name: string;
  onCancel: () => void;
  onConfirm: (newName: string) => void;
}) {
  const invalidName = (str: string) => {
    return str === '' || str === 'Untitled Project' || str === name;
  };
  const [newName, setNewName] = React.useState('');
  const [disableButton, setDisableButton] = React.useState(invalidName(name));
  const onChange = (newName: string) => {
    setDisableButton(invalidName(newName));
    setNewName(newName);
  };
  const onClickConfirm = (newName: string) => {
    setDisableButton(true);
    onConfirm(newName);
  };
  return (
    <Dialog
      title="Rename Dialog"
      onClose={onCancel}
      buttons={
        <>
          <Button label="Cancel" onClick={onCancel} size="large" />
          <Button
            variant="primary"
            label="Done"
            onClick={() => onClickConfirm(newName)}
            size="large"
            disabled={disableButton}
          />
        </>
      }
    >
      <TextSmallLight>
        <FormInput
          autoFocus
          type="textarea"
          value={newName}
          onChange={onChange}
          placeholder={name}
        />
      </TextSmallLight>
    </Dialog>
  );
}
function DemoVideoTile() {
  return (
    <div className={styles.project}>
      <a
        href="https://www.relume.io/resources/series-videos/introduction-getting-started"
        target="_blank"
        rel="noreferrer nofollow"
        className={classNames(styles.projectTile, styles.tutorialTile)}
      >
        <div className={styles.projectTileText}>
          <TextRegular lineClamp={1}>Getting Started Tutorials</TextRegular>
          <Inline spacing={8} yalign="center">
            <div className={styles.tutorialVideoIcon}>
              <IconVideo />
            </div>
            <TextSmallLight lineClamp={1}>10 videos</TextSmallLight>
          </Inline>
        </div>
        <div className={styles.tutorialLinkIcon}>
          <IconTop />
        </div>
      </a>
    </div>
  );
}
